import React, { useState } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { Info, ChevronDown, Check, Pause, X, Link, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from './SubscriptionAppButton';
import { TagInput } from './TagInput';
import type { TagData } from './TagInput';
import { useTags } from '../contexts/TagsContext';
import type { PackSize } from './PackSizesPanel';
interface PackSizeTier {
  id: string;
  quantity: number;
  label: string;
  color: string;
  loyaltyTier: string;
  discount: string;
}
interface TagItem {
  id: string;
  label: string;
}
interface TaggingEvent {
  id: string;
  title: string;
  description: string;
  icon: 'check' | 'pause' | 'cancel';
  tags: TagData[];
}
interface MembershipPanelProps {
  onNext?: () => void;
  onBack?: () => void;
  packSizes?: PackSize[];
}
export function MembershipPanel({
  onNext,
  onBack,
  packSizes = []
}: MembershipPanelProps) {
  const {
    allTags,
    createTag
  } = useTags();
  // Tier Mapping State
  const [packSizeTiers, setPackSizeTiers] = useState<PackSizeTier[]>([{
    id: '1',
    quantity: 3,
    label: 'Taster Pack',
    color: '#F59E0B',
    loyaltyTier: 'Bronze',
    discount: '5'
  }, {
    id: '2',
    quantity: 6,
    label: 'Half Dozen',
    color: '#0EA5E9',
    loyaltyTier: 'Silver',
    discount: '10'
  }, {
    id: '3',
    quantity: 12,
    label: 'Full Dozen',
    color: '#8B5CF6',
    loyaltyTier: 'Gold',
    discount: '15'
  }]);
  // Subscriber Discount Settings State
  const [eligibleProducts, setEligibleProducts] = useState('wine-only');
  const [discountStacking, setDiscountStacking] = useState('best-wins');
  const [checkoutMessage, setCheckoutMessage] = useState('Wine Club Member ({tier} — {percent}% off)');
  const [pausedBehaviour, setPausedBehaviour] = useState('reduced');
  // Auto-Tagging State
  const [taggingEvents, setTaggingEvents] = useState<TaggingEvent[]>([{
    id: 'subscribe',
    title: 'On Subscribe',
    description: 'Applied when a customer joins this box',
    icon: 'check',
    tags: [{
      id: 'subscription-active',
      label: 'subscription-active',
      color: 'blue'
    }, {
      id: 'tier-dynamic',
      label: 'tier-{dynamic}',
      color: 'blue'
    }, {
      id: 'winepik-subscriber',
      label: 'winepik_subscriber',
      color: 'green'
    }, {
      id: 'estate-club-member',
      label: 'estate-club-member',
      color: 'purple'
    }, {
      id: 'new-member',
      label: 'new-member',
      color: 'green'
    }]
  }, {
    id: 'pause',
    title: 'On Pause',
    description: 'Applied when a member pauses — subscription-active is removed automatically',
    icon: 'pause',
    tags: [{
      id: 'subscription-paused',
      label: 'subscription-paused',
      color: 'orange'
    }]
  }, {
    id: 'cancel',
    title: 'On Cancel',
    description: 'Applied when a member cancels — subscription-active and tier tags are removed automatically',
    icon: 'cancel',
    tags: [{
      id: 'subscription-cancelled',
      label: 'subscription-cancelled',
      color: 'gray'
    }, {
      id: 'winback-eligible',
      label: 'winback-eligible',
      color: 'orange'
    }]
  }]);
  // Tier badge rendering helper
  const TIER_COLORS: Record<string, string> = {
    Bronze: '#CD7F32',
    Silver: '#A0AEC0',
    Gold: '#D69E2E',
    Platinum: '#7C3AED'
  };
  const PACK_ICON_COLORS = ['#0EA5E9', '#8B5CF6', '#F59E0B', '#10B981'];
  const renderTierBadge = (tier: string | undefined) => {
    if (!tier || tier === 'None' || tier === '') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#F1F5F9] text-[#94A3B8]" data-id="element-1726">
          None
        </span>;
    }
    const color = TIER_COLORS[tier];
    if (color) {
      return <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#F8FAFC] border border-[#E2E8F0] text-[#0F172A]" data-id="element-1727">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{
          backgroundColor: color
        }} data-id="element-1728" />
          {tier}
        </span>;
    }
    // Custom tier
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#F1F5F9] border border-[#E2E8F0] text-[#475569]" data-id="element-1729">
        {tier}
      </span>;
  };
  const handleTierChange = (packId: string, field: 'loyaltyTier' | 'discount', value: string) => {
    setPackSizeTiers(prev => prev.map(pack => pack.id === packId ? {
      ...pack,
      [field]: value
    } : pack));
  };
  const handleRemoveTag = (eventId: string, tagId: string) => {
    setTaggingEvents(prev => prev.map(event => event.id === eventId ? {
      ...event,
      tags: event.tags.filter(tag => tag.id !== tagId)
    } : event));
  };
  const handleAddTag = (eventId: string, tag: TagData) => {
    setTaggingEvents(prev => prev.map(event => event.id === eventId ? {
      ...event,
      tags: event.tags.some(t => t.id === tag.id) ? event.tags : [...event.tags, tag]
    } : event));
  };
  const handleCreateTag = (eventId: string, label: string, color?: string) => {
    const newTag: TagData = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color: (color || 'blue') as TagData['color']
    };
    // Register in global tag system
    createTag(newTag);
    // Add to this event
    handleAddTag(eventId, newTag);
  };
  const getEventIcon = (icon: 'check' | 'pause' | 'cancel') => {
    switch (icon) {
      case 'check':
        return <div className="w-8 h-8 rounded-lg bg-[#D1FAE5] flex items-center justify-center" data-id="element-1730">
            <Check className="w-4 h-4 text-[#059669]" data-id="element-1731" />
          </div>;
      case 'pause':
        return <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center" data-id="element-1732">
            <Pause className="w-4 h-4 text-[#D97706]" data-id="element-1733" />
          </div>;
      case 'cancel':
        return <div className="w-8 h-8 rounded-lg bg-[#FEE2E2] flex items-center justify-center" data-id="element-1734">
            <X className="w-4 h-4 text-[#DC2626]" data-id="element-1735" />
          </div>;
    }
  };
  return <div className="flex flex-col gap-6 max-w-4xl mx-auto" data-id="element-1736">
      {/* Section 1: Tier Mapping */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-1737">
        <div className="p-6" data-id="element-1738">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-1739">
            Tier Mapping
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-1740">
            Tier assignments are configured per frequency in the Pack Sizes tab.
            This is a live summary.
          </p>

          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 mb-4" data-id="element-1741">
            <div className="col-span-5" data-id="element-1742">
              <span className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-1743">
                PACK / FREQUENCY
              </span>
            </div>
            <div className="col-span-4" data-id="element-1744">
              <span className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-1745">
                LOYALTY TIER
              </span>
            </div>
            <div className="col-span-3" data-id="element-1746">
              <span className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-1747">
                AD-HOC DISCOUNT
              </span>
            </div>
          </div>

          {/* Derived rows from packSizes */}
          {!packSizes || packSizes.length === 0 ? <div className="py-10 text-center text-[#94A3B8] bg-[#F8FAFC] rounded-lg border border-dashed border-[#E2E8F0]" data-id="element-1748">
              <p className="text-[14px]" data-id="element-1749">
                No pack sizes configured yet. Add pack sizes in the Pack Sizes
                tab to see tier assignments here.
              </p>
            </div> : <div className="space-y-3" data-id="element-1750">
              {packSizes.map((pack, packIndex) => pack.frequencies.map(freq => <div key={`${pack.id}-${freq.id}`} className="grid grid-cols-12 gap-4 items-center py-2 border-b border-[#F1F5F9] last:border-0" data-id="element-1751">
                    {/* Pack icon + name + frequency */}
                    <div className="col-span-5 flex items-center gap-3" data-id="element-1752">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-[13px] font-bold flex-shrink-0" style={{
                backgroundColor: PACK_ICON_COLORS[packIndex % PACK_ICON_COLORS.length]
              }} data-id="element-1753">
                        {pack.quantity || pack.number}
                      </div>
                      <div className="min-w-0" data-id="element-1754">
                        <div className="text-[14px] font-medium text-[#0F172A] truncate" data-id="element-1755">
                          {pack.displayLabel || pack.title}
                        </div>
                        <div className="text-[12px] text-[#94A3B8] truncate" data-id="element-1756">
                          {freq.interval || freq.name || 'No interval set'}
                        </div>
                      </div>
                    </div>

                    {/* Loyalty Tier badge (read-only) */}
                    <div className="col-span-4" data-id="element-1757">
                      {renderTierBadge(freq.loyaltyTier)}
                    </div>

                    {/* Ad-hoc discount (read-only) */}
                    <div className="col-span-3" data-id="element-1758">
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[6px] bg-[#F8FAFC] border border-[#E2E8F0] text-[14px] font-medium text-[#0F172A]" data-id="element-1759">
                        {freq.adHocDiscount ?? '0'}
                        <span className="text-[#64748B] text-[13px]" data-id="element-1760">%</span>
                      </span>
                    </div>
                  </div>))}
            </div>}

          {/* Info Banner */}
          <div className="mt-6 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-1761">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-1762" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-1763">
              The{' '}
              <span className="font-semibold text-[#0F172A]" data-id="element-1764">
                ad-hoc discount
              </span>{' '}
              applies automatically at checkout when a member purchases products
              outside their subscription box. Powered by a Shopify Discount
              Function — no codes required.
            </p>
          </div>
        </div>
      </Card>

      {/* Section 2: Subscriber Discount Settings */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-1765">
        <div className="p-6" data-id="element-1766">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-1767">
            Subscriber Discount Settings
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-1768">
            Configure how the ad-hoc member discount behaves at checkout
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1769">
            {/* Eligible Products */}
            <div data-id="element-1770">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1771">
                Eligible Products
              </label>
              <div className="relative" data-id="element-1772">
                <select value={eligibleProducts} onChange={e => setEligibleProducts(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-1773">
                  <option value="wine-only" data-id="element-1774">Wine products only</option>
                  <option value="all" data-id="element-1775">All products</option>
                  <option value="tagged" data-id="element-1776">Tagged products only</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-1777" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-1778">
                Which products the subscriber discount applies to. Merchandise
                and accessories can be excluded.
              </p>
            </div>

            {/* Discount Stacking */}
            <div data-id="element-1779">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1780">
                Discount Stacking
              </label>
              <div className="relative" data-id="element-1781">
                <select value={discountStacking} onChange={e => setDiscountStacking(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-1782">
                  <option value="best-wins" data-id="element-1783">Best discount wins</option>
                  <option value="stack" data-id="element-1784">Stack discounts</option>
                  <option value="member-only" data-id="element-1785">Member discount only</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-1786" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-1787">
                How the member discount interacts with sale prices, discount
                codes, and other automatic discounts.
              </p>
            </div>

            {/* Checkout Message */}
            <div data-id="element-1788">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1789">
                Checkout Message
              </label>
              <input type="text" value={checkoutMessage} onChange={e => setCheckoutMessage(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-1790" />

              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-1791">
                Displayed at checkout. Use {'{tier}'} and {'{percent}'} as
                dynamic placeholders.
              </p>
            </div>

            {/* Paused Subscriber Behaviour */}
            <div data-id="element-1792">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-1793">
                Paused Subscriber Behaviour
              </label>
              <div className="relative" data-id="element-1794">
                <select value={pausedBehaviour} onChange={e => setPausedBehaviour(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-1795">
                  <option value="reduced" data-id="element-1796">Reduced discount while paused</option>
                  <option value="none" data-id="element-1797">No discount while paused</option>
                  <option value="full" data-id="element-1798">Full discount while paused</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-1799" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#0EA5E9]" data-id="element-1800">
                What happens to the ad-hoc discount when a member pauses their
                subscription.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 3: Auto-Tagging */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-1801">
        <div className="p-6" data-id="element-1802">
          <div className="flex items-start justify-between mb-1" data-id="element-1803">
            <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-1804">
              Auto-Tagging
            </h2>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#64748B] bg-white border border-[#E2E8F0] rounded-md hover:bg-[#F8FAFC] transition-colors" data-id="element-1805">
              <Link className="w-3.5 h-3.5" data-id="element-1806" />
              Extends global tagging rules
            </button>
          </div>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-1807">
            Tags applied to Shopify customers based on subscription lifecycle
            events. These sync to Klaviyo for segmentation.
          </p>

          <div className="space-y-6" data-id="element-1808">
            {taggingEvents.map((event, index) => <div key={event.id} data-id="element-1809">
                {index > 0 && <div className="h-px bg-[#E2E8F0] mb-6" data-id="element-1810" />}

                <div className="flex items-start gap-3 mb-4" data-id="element-1811">
                  {getEventIcon(event.icon)}
                  <div data-id="element-1812">
                    <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-1813">
                      {event.title}
                    </h3>
                    <p className="text-[13px] text-[#94A3B8]" data-id="element-1814">
                      {event.description}
                    </p>
                  </div>
                </div>

                {/* Tags - using TagInput component */}
                <TagInput tags={event.tags} availableTags={allTags} onAddTag={tag => handleAddTag(event.id, tag)} onRemoveTag={tagId => handleRemoveTag(event.id, tagId)} onCreateTag={(label, color) => handleCreateTag(event.id, label, color)} placeholder="Search or create a tag..." data-id="element-1815" />
              </div>)}
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between pt-2" data-id="element-1816">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-1817">
          <ArrowLeft className="w-4 h-4" data-id="element-1818" />
          Pack Sizes
        </button>
        <Button variant="primary" onClick={onNext} iconRight={<ArrowRight className="w-4 h-4" data-id="element-1820" />} data-id="element-1819">
          Next: Flow
        </Button>
      </div>
    </div>;
}